/**
 * Shared navigation helper for programmatic navigation.
 *
 * Delegates to the module-level router instance created by initRouter().
 * Before the router is initialized (e.g. during SSR or early app init),
 * falls back to window.location APIs.
 *
 * navigateTo(), AuthProvider, and onAccountSwitch() call history.push() /
 * history.replace() which delegate to router.navigate() on the TanStack Router instance.
 */

import { getRouter } from '../router/router';

export type HistoryNavigateOptions = {
  // oxlint-disable-next-line typescript/no-explicit-any -- intentionally retained: router state is untyped (TanStack Router's navigate accepts any)
  state?: any;
  /** URL search params to pass to router.navigate (e.g. { bin, acct }) */
  search?: Record<string, string | undefined>;
};

/**
 * history-compatible API for programmatic navigation.
 * The navigateTo() utility, AuthProvider, and onAccountSwitch() call
 * `history.push(path, options)` and `history.replace(path, options)`.
 *
 * Options:
 * - `search`: URL search params forwarded to router.navigate (e.g. { bin, acct })
 * - `state`: Router state (unused currently but supported for future use)
 *
 * When called without options, navigates to the path with no search params or state.
 */
export const history = {
  push: (path: string, options?: HistoryNavigateOptions) => {
    const router = getRouter();
    if (router) {
      const { state, search } = options ?? {};
      // oxlint-disable-next-line typescript/no-explicit-any -- intentionally retained: router.navigate's generic overloads make precise typing impractical
      const navOptions: any = { to: path, state };
      if (search) navOptions.search = search;
      void router.navigate(navOptions);
    } else {
      window.location.href = path;
    }
  },
  replace: (path: string, options?: HistoryNavigateOptions) => {
    const router = getRouter();
    if (router) {
      const { state, search } = options ?? {};
      // oxlint-disable-next-line typescript/no-explicit-any -- intentionally retained: router.navigate's generic overloads make precise typing impractical
      const navOptions: any = { to: path, replace: true, state };
      if (search) navOptions.search = search;
      void router.navigate(navOptions);
    } else {
      window.location.replace(path);
    }
  },
  go: (delta: number) => {
    const router = getRouter();
    if (router) {
      router.history.go(delta);
    } else {
      window.history.go(delta);
    }
  },
  goBack: () => {
    history.go(-1);
  },
  goForward: () => {
    history.go(1);
  },
};
